import { Scenario, CollisionError } from '../types';

/**
 * Проверяет коллизии между включёнными сценариями:
 * 1. Дублирование ID шагов
 * 2. Циклические зависимости
 * 3. Dangling edges (связи на несуществующие шаги)
 * 4. Orphan steps (шаги без связей)
 * 5. Невалидные зоны
 */
export function detectCollisions(scenarios: Scenario[]): CollisionError[] {
  const errors: CollisionError[] = [];
  const visible = scenarios.filter(s => s.visible);

  // 1. Collect all step IDs and check duplicates
  const stepIds = new Map<string, string>(); // stepId → scenarioId
  for (const sc of visible) {
    for (const step of sc.diagram.steps) {
      const existing = stepIds.get(step.id);
      if (existing && existing !== sc.id) {
        errors.push({
          type: 'duplicate_id',
          message: `Шаг "${step.id}" дублируется в сценариях ${existing} и ${sc.id}`,
          scenarioId: sc.id,
          stepId: step.id,
        });
      }
      stepIds.set(step.id, sc.id);
    }
  }

  // 2. Check each scenario individually
  for (const sc of visible) {
    const localStepIds = new Set(sc.diagram.steps.map(s => s.id));
    const laneIds = new Set(sc.diagram.lanes.map(l => l.id));

    // 2a. Dangling edges
    for (const conn of sc.diagram.connections) {
      if (!localStepIds.has(conn.from)) {
        errors.push({
          type: 'dangling_edge',
          message: `Сценарий ${sc.id}: связь из "${conn.from}" → "${conn.to}" — шаг "${conn.from}" не найден`,
          scenarioId: sc.id,
        });
      }
      if (!localStepIds.has(conn.to)) {
        errors.push({
          type: 'dangling_edge',
          message: `Сценарий ${sc.id}: связь из "${conn.from}" → "${conn.to}" — шаг "${conn.to}" не найден`,
          scenarioId: sc.id,
        });
      }
    }

    // 2b. Invalid lanes
    for (const step of sc.diagram.steps) {
      if (!laneIds.has(step.laneId)) {
        errors.push({
          type: 'invalid_lane',
          message: `Сценарий ${sc.id}: шаг "${step.id}" ссылается на несуществующую зону "${step.laneId}"`,
          scenarioId: sc.id,
          stepId: step.id,
        });
      }
    }

    // 2c. Orphan steps (no incoming AND no outgoing connections, except start/end)
    const hasIncoming = new Set<string>();
    const hasOutgoing = new Set<string>();
    for (const conn of sc.diagram.connections) {
      hasOutgoing.add(conn.from);
      hasIncoming.add(conn.to);
    }
    for (const step of sc.diagram.steps) {
      if (step.type === 'start' && !hasOutgoing.has(step.id)) {
        errors.push({
          type: 'orphan_step',
          message: `Сценарий ${sc.id}: начальный шаг "${step.id}" не имеет исходящих связей`,
          scenarioId: sc.id,
          stepId: step.id,
        });
      }
      if (step.type === 'end' && !hasIncoming.has(step.id)) {
        errors.push({
          type: 'orphan_step',
          message: `Сценарий ${sc.id}: конечный шаг "${step.id}" не имеет входящих связей`,
          scenarioId: sc.id,
          stepId: step.id,
        });
      }
      if (step.type !== 'start' && step.type !== 'end' &&
          !hasIncoming.has(step.id) && !hasOutgoing.has(step.id)) {
        errors.push({
          type: 'orphan_step',
          message: `Сценарий ${sc.id}: шаг "${step.id}" изолирован (нет связей)`,
          scenarioId: sc.id,
          stepId: step.id,
        });
      }
    }

    // 2d. Cycle detection (DFS)
    const adj = new Map<string, string[]>();
    for (const conn of sc.diagram.connections) {
      if (!adj.has(conn.from)) adj.set(conn.from, []);
      adj.get(conn.from)!.push(conn.to);
    }
    const visited = new Set<string>();
    const recStack = new Set<string>();
    const hasCycle = (node: string): boolean => {
      visited.add(node);
      recStack.add(node);
      for (const neighbor of adj.get(node) || []) {
        if (!visited.has(neighbor)) {
          if (hasCycle(neighbor)) return true;
        } else if (recStack.has(neighbor)) {
          return true;
        }
      }
      recStack.delete(node);
      return false;
    };
    for (const step of sc.diagram.steps) {
      if (!visited.has(step.id) && hasCycle(step.id)) {
        errors.push({
          type: 'cycle',
          message: `Сценарий ${sc.id}: обнаружена циклическая зависимость (включает шаг "${step.id}")`,
          scenarioId: sc.id,
          stepId: step.id,
        });
        break; // One cycle error per scenario is enough
      }
    }
  }

  return errors;
}
